import { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Modal, Pressable } from 'react-native';
import Svg, { Circle, Text as SvgText } from 'react-native-svg';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useTheme } from '../../../contexts/ThemeContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useResponsive } from '../../../hooks/useResponsive';
import { FONTS } from '../../../constants/config';
import { createShadow } from '../../../utils/styles';
import { Specialization } from '../types';
import { MainStackParamList } from '../../../navigation/MainNavigator';

const MUTED = '#64748B';

type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

interface SpecializationsSectionProps {
  specializations: Specialization[];
  onDeleteSpecialization?: (specId: number) => void;
  onCancelRequest?: (studentId: number, linkId: number) => void;
  isStrategyTeam?: boolean;
  isOrgEmployee?: boolean;
  isStudent?: boolean;
  onAddSpecialization?: () => void;
}

function CircularProgress({ percentage, size = 64, strokeWidth = 6 }: {
  percentage: number;
  size?: number;
  strokeWidth?: number;
}) {
  const { isDark } = useTheme();
  const center = size / 2;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  const isLow = percentage < 75;
  const trackColor = isLow
    ? (isDark ? '#1E3A5F' : '#BFDBFE')
    : (isDark ? '#374151' : '#E5E7EB');
  const progressColor = isLow ? '#F59E0B' : '#22C55E';

  return (
    <View style={{ width: size, height: size }}>
      <Svg width={size} height={size}>
        <Circle
          cx={center} cy={center} r={radius}
          stroke={trackColor} strokeWidth={strokeWidth} fill="none"
        />
        <Circle
          cx={center} cy={center} r={radius}
          stroke={progressColor} strokeWidth={strokeWidth} fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          rotation={-90}
          origin={`${center}, ${center}`}
        />
        <SvgText
          x={center} y={center + 5}
          textAnchor="middle"
          fontSize={13} fontFamily={FONTS.medium}
          fill={isDark ? '#F1F5F9' : '#303030'}
        >
          {`%${percentage}`}
        </SvgText>
      </Svg>
    </View>
  );
}

// ============================================================
// Distribution Bar Chart (matches Figma exactly)
// ============================================================

const DIST_CHART_HEIGHT = 170;

function computeYScale(_specializations: Specialization[]) {
  // Fixed ticks matching Figma: 0, 5, 10, 15
  return { yMax: 15, ticks: [0, 5, 10, 15] };
}

function DistributionBarChart({ specializations, isRTL }: {
  specializations: Specialization[];
  isRTL: boolean;
}) {
  const { colors, isDark } = useTheme();
  const totalSpecializations = specializations.length;
  const { yMax: Y_MAX, ticks: Y_TICKS } = computeYScale(specializations);

  return (
    <View style={[
      styles.chartCard,
      {
        backgroundColor: isDark ? colors.card : '#FFFFFF',
        borderColor: isDark ? colors.cardBorder : '#E2E8F0',
      },
      createShadow(0, 1, 3, '#000', 0.1),
    ]}>
      {/* Title */}
      <Text style={[
        styles.chartTitle,
        { color: isDark ? colors.textPrimary : '#414042', textAlign: isRTL ? 'right' : 'left' },
      ]}>
        {'توزيع المشاركين وفق الموهبة'}
      </Text>

      {/* Chart + Summary */}
      <View style={styles.chartRow}>
        {/* Bar Chart Area */}
        <View style={styles.chartArea}>
          <View style={distStyles.container}>
            {/* Y-axis labels */}
            <View style={distStyles.yColumn}>
              <View style={distStyles.yChartPart}>
                {Y_TICKS.slice().reverse().map((val) => (
                  <Text key={val} style={[distStyles.yLabel, { bottom: `${(val / Y_MAX) * 100}%` }]}>
                    {val}
                  </Text>
                ))}
              </View>
              <View style={distStyles.yLabelSpacer} />
            </View>

            {/* Bars area */}
            <View style={distStyles.barsArea}>
              {/* Chart border (bottom) */}
              <View style={distStyles.chartBorder}>
                {Y_TICKS.slice().reverse().map((val) => (
                  <View key={val} style={[distStyles.gridLine, { bottom: `${(val / Y_MAX) * 100}%` }]} />
                ))}
              </View>

              {/* Bar columns */}
              <View style={[distStyles.columnsRow, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                {specializations.map((spec, idx) => {
                  const heightPct = Y_MAX > 0 ? (spec.participantCount / Y_MAX) * 100 : 0;
                  return (
                    <View key={spec.id} style={distStyles.columnWithDivider}>
                      {idx > 0 && <View style={distStyles.verticalDivider} />}
                      <View style={distStyles.column}>
                        <View style={distStyles.barTrack}>
                          {heightPct > 0 && (
                            <View style={[distStyles.bar, { backgroundColor: spec.color, height: `${heightPct}%` }]} />
                          )}
                        </View>
                        <Text style={distStyles.barLabel} numberOfLines={2}>
                          {spec.title}
                        </Text>
                      </View>
                    </View>
                  );
                })}
              </View>
            </View>
          </View>
        </View>

        {/* Summary Box (right side) */}
        <View style={[
          styles.summaryBox,
          { backgroundColor: isDark ? colors.backgroundAlt : '#F1F5F9' },
        ]}>
          <Text style={[styles.summaryLabel, { color: isDark ? colors.textSecondary : '#414042', textAlign: 'center' }]}>
            {'إجمالي عدد المواهب'}
          </Text>
          <Text style={[styles.summaryValue, { color: isDark ? colors.textPrimary : '#414042' }]}>
            {totalSpecializations}
          </Text>
        </View>
      </View>
    </View>
  );
}

// ============================================================
// Main Section Component
// ============================================================

export default function SpecializationsSection({ specializations, onDeleteSpecialization, onCancelRequest, isStrategyTeam, isOrgEmployee, isStudent, onAddSpecialization }: SpecializationsSectionProps) {
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const { isMobile } = useResponsive();
  const navigation = useNavigation<NavigationProp>();
  const [cancelConfirm, setCancelConfirm] = useState<{ studentId: number; linkId: number; specName: string } | null>(null);

  const handleViewDetails = (specId: number, specName: string) => {
    navigation.navigate('SpecializationDetail', { id: specId, name: specName });
  };


  return (
    <View style={styles.container}>
      {/* Section Header */}
      <View style={[
        styles.sectionHeader,
        { flexDirection: isRTL ? 'row-reverse' : 'row' },
      ]}>
        <Text style={[styles.sectionTitle, { color: colors.textPrimary, textAlign: isRTL ? 'right' : 'left' }]}>
          {isStudent ? (isRTL ? 'أنواع المواهب' : 'Talent Types') : t('talentsPage.specializations')}
        </Text>
        {isStrategyTeam && !isStudent && (
          <TouchableOpacity
            style={[styles.addSpecBtn, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}
            onPress={onAddSpecialization}
          >
            <Ionicons name="add-circle-outline" size={18} color="#414042" />
            <Text style={styles.addSpecText}>
              {isRTL ? 'إضافة موهبة' : 'Add Specialization'}
            </Text>
          </TouchableOpacity>
        )}
        {isStudent && (
          <TouchableOpacity
            style={[styles.addSpecBtn, { flexDirection: isRTL ? 'row-reverse' : 'row', borderWidth: 1, borderColor: '#E2E8F0', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 6 }]}
            onPress={() => navigation.navigate('StudentTalentRegister' as any)}
          >
            <Ionicons name="add-outline" size={16} color="#414042" />
            <Text style={styles.addSpecText}>
              {isRTL ? 'التسجيل في موهبة' : 'Register in Talent'}
            </Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Background box containing cards */}
      <View style={[
        styles.cardsContainer,
        {
          backgroundColor: isDark ? colors.background : '#F8FAFC',
          borderColor: isDark ? colors.cardBorder : '#E2E8F0',
        },
      ]}>
      {specializations.length === 0 ? (
        <View style={styles.emptyState}>
          <Text style={[styles.emptyText, { color: colors.textMuted || MUTED }]}>
            {isRTL ? 'لا توجد مواهب حالياً' : 'No talents available'}
          </Text>
        </View>
      ) : (
      <View style={[
        styles.cardsRow,
        {
          flexDirection: isMobile ? 'column' : (isRTL ? 'row-reverse' : 'row'),
        },
      ]}>
        {specializations.map((spec) => (
          <View
            key={spec.id}
            style={[
              styles.card,
              {
                backgroundColor: isDark ? colors.card : '#FFFFFF',
                borderColor: isDark ? colors.cardBorder : '#E2E8F0',
                width: isMobile ? '100%' : '23%',
              },
              createShadow(0, 1, 3, '#000', 0.1),
            ]}
          >
            <View style={styles.cardContent}>
              {isStudent ? (
                /* Student view: simplified card — name + status tag + two counts */
                <>
                  <View style={{ flexDirection: isRTL ? 'row-reverse' : 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <Text
                      style={[styles.cardTitle, { color: colors.textPrimary, textAlign: isRTL ? 'right' : 'left', fontSize: 15, flex: 1 }]}
                      numberOfLines={2}
                    >
                      {spec.title}
                    </Text>
                    {spec.linkStatus === 'applied' && (
                      <View style={{ backgroundColor: '#FFEDD5', borderRadius: 12, paddingHorizontal: 10, paddingVertical: 3 }}>
                        <Text style={{ fontFamily: 'JF-Flat-Regular', fontSize: 11, color: '#9A3412' }}>بإنتظار الموافقة</Text>
                      </View>
                    )}
                    {spec.linkStatus === 'proposed' && (
                      <View style={{ backgroundColor: '#DBEAFE', borderRadius: 12, paddingHorizontal: 10, paddingVertical: 3 }}>
                        <Text style={{ fontFamily: 'JF-Flat-Regular', fontSize: 11, color: '#1E40AF' }}>بإنتظار الاعتماد</Text>
                      </View>
                    )}
                    {(spec.linkStatus === 'active' || !spec.linkStatus) && (
                      <View style={{ backgroundColor: '#DCFCE7', borderRadius: 12, paddingHorizontal: 10, paddingVertical: 3 }}>
                        <Text style={{ fontFamily: 'JF-Flat-Regular', fontSize: 11, color: '#16A34A' }}>نشط</Text>
                      </View>
                    )}
                    {spec.linkStatus === 'rejected' && (
                      <View style={{ backgroundColor: '#FEE2E2', borderRadius: 12, paddingHorizontal: 10, paddingVertical: 3 }}>
                        <Text style={{ fontFamily: 'JF-Flat-Regular', fontSize: 11, color: '#DC2626' }}>مرفوض</Text>
                      </View>
                    )}
                    {spec.linkStatus === 'withdrawn' && (
                      <View style={{ backgroundColor: '#FFEDD5', borderRadius: 12, paddingHorizontal: 10, paddingVertical: 3 }}>
                        <Text style={{ fontFamily: 'JF-Flat-Regular', fontSize: 11, color: '#E97A1F' }}>منسحب</Text>
                      </View>
                    )}
                  </View>
                  <View style={[styles.statsRow, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                    <View style={[styles.statPill, { backgroundColor: isDark ? colors.backgroundAlt : '#F1F5F9' }]}>
                      <Text style={[styles.statLabel, { color: colors.textMuted }]}>
                        {t('talentsPage.participants')}
                      </Text>
                      <Text style={[styles.statValue, { color: colors.textPrimary }]}>
                        {spec.participantCount}
                      </Text>
                    </View>
                    <View style={[styles.statPill, { backgroundColor: isDark ? colors.backgroundAlt : '#F1F5F9' }]}>
                      <Text style={[styles.statLabel, { color: colors.textMuted }]}>
                        {t('talentsPage.activities')}
                      </Text>
                      <Text style={[styles.statValue, { color: colors.textPrimary }]}>
                        {spec.activityCount}
                      </Text>
                    </View>
                  </View>
                </>
              ) : (
                /* Normal view: progress circle + title + three stat pills */
                <>
                  <View style={[styles.topRow, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                    <CircularProgress percentage={spec.progress} size={64} strokeWidth={6} />
                    <View style={[styles.titleContainer, { flex: 1 }]}>
                      <Text
                        style={[styles.cardTitle, { color: colors.textPrimary, textAlign: isRTL ? 'right' : 'left' }]}
                        numberOfLines={2}
                      >
                        {spec.title}
                      </Text>
                    </View>
                    {spec.isPublished === false && (
                      <View style={{ backgroundColor: '#FFEDD5', borderRadius: 16, paddingHorizontal: 12, paddingVertical: 6, alignSelf: 'center' }}>
                        <Text style={{ fontFamily: 'JF-Flat-Regular', fontSize: 11, color: '#9A3412' }}>بانتظار النشر</Text>
                      </View>
                    )}
                  </View>
                  <View style={[styles.statsRow, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                    <View style={[styles.statPill, { backgroundColor: isDark ? colors.backgroundAlt : '#F1F5F9' }]}>
                      <Text style={[styles.statLabel, { color: colors.textMuted }]}>
                        {t('talentsPage.kpis')}
                      </Text>
                      <Text style={[styles.statValue, { color: colors.textPrimary }]}>
                        {spec.kpiCount}
                      </Text>
                    </View>
                    <View style={[styles.statPill, { backgroundColor: isDark ? colors.backgroundAlt : '#F1F5F9' }]}>
                      <Text style={[styles.statLabel, { color: colors.textMuted }]}>
                        {t('talentsPage.activities')}
                      </Text>
                      <Text style={[styles.statValue, { color: colors.textPrimary }]}>
                        {spec.activityCount}
                      </Text>
                    </View>
                    <View style={[styles.statPill, { backgroundColor: isDark ? colors.backgroundAlt : '#F1F5F9' }]}>
                      <Text style={[styles.statLabel, { color: colors.textMuted }]}>
                        {t('talentsPage.participants')}
                      </Text>
                      <Text style={[styles.statValue, { color: colors.textPrimary }]}>
                        {spec.participantCount}
                      </Text>
                    </View>
                  </View>
                </>
              )}

              {/* Footer */}
              <View style={[styles.cardFooter, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                {/* Rejected students: no detail access. Withdrawn: read-only access */}
                {isStudent && spec.linkStatus === 'rejected' ? (
                  <Text style={[styles.detailsText, { color: '#94A3B8' }]}>
                    {isRTL ? 'غير متاح' : 'Not available'}
                  </Text>
                ) : (
                <TouchableOpacity
                  style={[styles.detailsLink, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}
                  onPress={() => handleViewDetails(spec.id, spec.title)}
                >
                  <Text style={[styles.detailsText, { color: colors.textSecondary }]}>
                    {t('talentsPage.viewDetails')}
                  </Text>
                  <Ionicons
                    name={isRTL ? 'chevron-back' : 'chevron-forward'}
                    size={14}
                    color={colors.textSecondary}
                  />
                </TouchableOpacity>
                )}
                {isStrategyTeam && (
                  <View style={{ flexDirection: isRTL ? 'row-reverse' : 'row', alignItems: 'center', gap: 8 }}>
                    <TouchableOpacity onPress={() => navigation.navigate('EditTalents', { mode: 'talents' })}>
                      <Ionicons name="create-outline" size={18} color={colors.textSecondary} />
                    </TouchableOpacity>
                    <TouchableOpacity onPress={() => onDeleteSpecialization?.(spec.id)}>
                      <Ionicons name="trash-outline" size={18} color="#DC2626" />
                    </TouchableOpacity>
                  </View>
                )}
                {isOrgEmployee && !isStrategyTeam && (
                  <TouchableOpacity onPress={() => navigation.navigate('EditTalents', { mode: 'schedule', specId: spec.id })}>
                    <Ionicons name="create-outline" size={18} color={colors.textSecondary} />
                  </TouchableOpacity>
                )}
                {isStudent && (spec.linkStatus === 'applied' || spec.linkStatus === 'proposed') && spec.linkId && spec.studentId && (
                  <TouchableOpacity
                    style={{ flexDirection: isRTL ? 'row-reverse' : 'row', alignItems: 'center', gap: 4 }}
                    onPress={() => setCancelConfirm({ studentId: spec.studentId!, linkId: spec.linkId!, specName: spec.title })}
                  >
                    <Ionicons name="close-outline" size={16} color="#DC2626" />
                    <Text style={{ fontFamily: FONTS.regular, fontSize: 13, color: '#DC2626' }}>إلغاء الطلب</Text>
                  </TouchableOpacity>
                )}
              </View>
            </View>
          </View>
        ))}
      </View>
      )}
      </View>

      {/* Cancel Request Confirmation Modal */}
      <Modal visible={cancelConfirm !== null} transparent animationType="fade">
        <Pressable style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.4)', justifyContent: 'center', alignItems: 'center' }} onPress={() => setCancelConfirm(null)}>
          <Pressable style={{ backgroundColor: '#FFFFFF', borderRadius: 16, padding: 24, width: 400, maxWidth: '90%' }} onPress={(e) => e.stopPropagation()}>
            <Ionicons name="warning-outline" size={40} color="#EAB308" style={{ alignSelf: 'center', marginBottom: 12 }} />
            <Text style={{ fontSize: 18, fontFamily: FONTS.medium, color: '#414042', textAlign: 'center', marginBottom: 8 }}>
              {isRTL ? 'إلغاء الطلب' : 'Cancel Request'}
            </Text>
            <Text style={{ fontSize: 14, fontFamily: FONTS.regular, color: '#64748B', textAlign: 'center', lineHeight: 22, marginBottom: 20 }}>
              {isRTL
                ? `هل أنت متأكد من إلغاء طلب التسجيل في "${cancelConfirm?.specName}"؟ يمكنك التقديم مرة أخرى لاحقاً.`
                : `Are you sure you want to cancel your application for "${cancelConfirm?.specName}"? You can re-apply later.`}
            </Text>
            <View style={{ flexDirection: 'row', gap: 12, justifyContent: 'center' }}>
              <TouchableOpacity
                style={{ flex: 1, borderWidth: 1, borderColor: '#CBD5E1', borderRadius: 8, paddingVertical: 10, alignItems: 'center' }}
                onPress={() => setCancelConfirm(null)}
              >
                <Text style={{ fontSize: 14, fontFamily: FONTS.medium, color: '#414042' }}>{isRTL ? 'تراجع' : 'Go Back'}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={{ flex: 1, backgroundColor: '#DC2626', borderRadius: 8, paddingVertical: 10, alignItems: 'center' }}
                onPress={() => {
                  if (cancelConfirm) {
                    onCancelRequest?.(cancelConfirm.studentId, cancelConfirm.linkId);
                  }
                  setCancelConfirm(null);
                }}
              >
                <Text style={{ fontSize: 14, fontFamily: FONTS.medium, color: '#FFFFFF' }}>{isRTL ? 'إلغاء الطلب' : 'Cancel Request'}</Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 12,
  },
  cardsContainer: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 16,
    minHeight: 180,
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 140,
  },
  emptyText: {
    fontSize: 15,
    fontFamily: FONTS.regular,
  },
  sectionHeader: {
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  sectionTitle: {
    fontSize: 20,
    fontFamily: FONTS.medium,
  },
  addSpecBtn: {
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#F1F5F9',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  addSpecText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
  },

  // Cards row layout
  cardsRow: {
    flexWrap: 'wrap',
    gap: 12,
  },
  card: {
    borderRadius: 8,
    borderWidth: 1,
    padding: 12,
    minWidth: 0,
  },
  cardContent: {
    gap: 16,
  },
  topRow: {
    alignItems: 'center',
    gap: 12,
  },
  titleContainer: {
    flex: 1,
  },
  cardTitle: {
    fontSize: 14,
    fontFamily: FONTS.regular,
  },
  statsRow: {
    gap: 8,
  },
  statPill: {
    flex: 1,
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 2,
    alignItems: 'center',
    gap: 4,
  },
  statLabel: {
    fontSize: 12,
    fontFamily: FONTS.regular,
  },
  statValue: {
    fontSize: 14,
    fontFamily: FONTS.medium,
  },
  cardFooter: {
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTopWidth: 1,
    borderTopColor: '#E2E8F0',
    paddingTop: 10,
  },
  detailsLink: {
    alignItems: 'center',
    gap: 4,
  },
  detailsText: {
    fontSize: 13,
    fontFamily: FONTS.regular,
  },
  // Distribution Bar Chart
  chartCard: {
    borderRadius: 8,
    borderWidth: 1,
    padding: 20,
    gap: 20,
  },
  chartTitle: {
    fontSize: 16,
    fontFamily: FONTS.regular,
  },
  chartRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: 16,
  },
  chartArea: {
    flex: 1,
  },
  summaryBox: {
    borderRadius: 8,
    padding: 12,
    width: 130,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    alignSelf: 'stretch',
  },
  summaryLabel: {
    fontSize: 14,
    fontFamily: FONTS.regular,
  },
  summaryValue: {
    fontSize: 24,
    fontFamily: FONTS.medium,
  },
});

const distStyles = StyleSheet.create({
  container: {
    flexDirection: 'row',
  },
  yColumn: {
    width: 28,
  },
  yChartPart: {
    height: DIST_CHART_HEIGHT,
    position: 'relative',
  },
  yLabel: {
    position: 'absolute',
    right: 4,
    fontSize: 10,
    fontFamily: FONTS.regular,
    lineHeight: 14,
    textAlign: 'right',
    color: '#737373',
    transform: [{ translateY: 7 }],
  },
  yLabelSpacer: {
    height: 34,
  },
  barsArea: {
    flex: 1,
    position: 'relative',
  },
  chartBorder: {
    height: DIST_CHART_HEIGHT,
    position: 'relative',
    borderBottomWidth: 1,
    borderBottomColor: '#d4d4d4',
  },
  gridLine: {
    position: 'absolute',
    left: 0,
    right: 0,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: '#d4d4d4',
  },
  columnsRow: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'space-evenly',
    paddingHorizontal: 8,
    gap: 8,
  },
  columnWithDivider: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'stretch',
  },
  verticalDivider: {
    width: StyleSheet.hairlineWidth,
    backgroundColor: '#d4d4d4',
    marginTop: 0,
    marginBottom: 34,
  },
  column: {
    flex: 1,
    alignItems: 'center',
  },
  barTrack: {
    width: 70,
    height: DIST_CHART_HEIGHT,
    justifyContent: 'flex-end',
  },
  bar: {
    width: '100%',
    borderTopLeftRadius: 2,
    borderTopRightRadius: 2,
    minHeight: 2,
  },
  barLabel: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    lineHeight: 15,
    textAlign: 'center',
    marginTop: 8,
    color: '#64748b',
  },
});
